import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "react-toastify";
import { useNavigate } from "react-router-dom";
import { store, persistor } from "../../store/store";
import { FiEye, FiEyeOff } from "react-icons/fi";
import { useSelector } from "react-redux";

export default function ChangePassword() {
    const [current, setCurrent] = useState("");
    const [newPass, setNewPass] = useState("");
    const [confirm, setConfirm] = useState("");
    const [strength, setStrength] = useState("");
    const [matchMsg, setMatchMsg] = useState("");
    const authData = useSelector(state => state.authToken?.authData);
    const [userIdAuth, setUserIdAuth] = useState(null);
    const [showCurrent, setShowCurrent] = useState(false);
    const [showNew, setShowNew] = useState(false);
    const [showConfirm, setShowConfirm] = useState(false);
    const navigate = useNavigate();
    useEffect(() => {
        if (authData?.userId) {
            setUserIdAuth(authData.userId);
        }
    }, [authData]);

    // 🔹 Password strength check
    const checkStrength = (value) => {
        setNewPass(value);

        const strongRegex =
            /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&]).{8,}$/;

        if (!value) {
            setStrength("");
        } else if (strongRegex.test(value)) {
            setStrength("Strong password 💪");
        } else {
            setStrength(
                "Weak: 8 chars, uppercase, lowercase, number & special char required"
            );
        }
    };

    // 🔹 Password match check
    const checkMatch = (value) => {
        setConfirm(value);

        if (!value) {
            setMatchMsg("");
        } else if (value === newPass) {
            setMatchMsg("Passwords match ✔");
        } else {
            setMatchMsg("Passwords do not match ❌");
        }
    };

    // 🔹 Submit handler
    const handleSubmit = () => {
        console.log("Submitting:", { current, newPass, confirm, userIdAuth });
        if (!current || !newPass || !confirm) {
            alert("All fields are required");
            toast.error("All fields are required");
            return;
        }

        if (newPass !== confirm) {
            alert("Passwords do not match");
            toast.error("Passwords do not match");
            return;
        }

        if (!authData?.UserID) {
            // toast.error("User not authenticated");
            return;
        }

        changePassword();
    };

    // 🔹 API call
    const changePassword = async () => {
        const url = "https://urbangis.mp.gov.in/api/User/ChangeUserPassword";
        const authToken = localStorage.getItem("token");

        const payload = {
            userId: authData?.UserID,
            oldPassword: current,
            newPassword: newPass,
        };

        try {
            const response = await axios.post(url, payload, {
                headers: {
                    "Content-Type": "application/json",
                    Authorization: `Bearer ${authToken}`,
                },
            });

            console.log("Change password response:", response.data);

            toast.success(response.message || "Password changed successfully");

            // 🔹 Logout after password change
            localStorage.clear();
            await persistor.purge();
            store.dispatch({ type: "RESET_ALL_STATE" });
            navigate("/login");

        } catch (error) {
            console.error("Change password error:", error);
            toast.error(
                error.response?.data?.message || "Failed to change password"
            );
        }
    };

    // 🔹 Inline styles
    const styles = {
        card: {
            maxWidth: "420px",
            margin: "50px auto",
            padding: "25px 30px",
            background: "#fff",
            borderRadius: "12px",
            boxShadow: "0 10px 25px rgba(0,0,0,0.1)",
            fontFamily: "Segoe UI, sans-serif",
        },
        title: {
            textAlign: "center",
            marginBottom: "20px",
            color: "#333",
        },
        group: {
            marginBottom: "18px",
        },
        label: {
            display: "block",
            marginBottom: "6px",
            fontSize: "14px",
            fontWeight: "600",
            color: "#555",
        },
        inputWrapper: {
            position: "relative",
        },
        input: {
            width: "100%",
            padding: "10px 40px 10px 12px",
            border: "1px solid #ddd",
            borderRadius: "6px",
            fontSize: "14px",
            outline: "none",
        },
        eye: {
            position: "absolute",
            right: "12px",
            top: "50%",
            transform: "translateY(-50%)",
            cursor: "pointer",
            color: "#666",
            fontSize: "18px",
        },
        success: {
            color: "#28a745",
            fontSize: "12px",
            marginTop: "6px",
            display: "block",
        },
        error: {
            color: "#dc3545",
            fontSize: "12px",
            marginTop: "6px",
            display: "block",
        },
        button: {
            width: "100%",
            padding: "12px",
            background: "linear-gradient(135deg, #626a70, #033e3b)",
            border: "none",
            borderRadius: "8px",
            color: "#fff",
            fontSize: "15px",
            fontWeight: "600",
            cursor: "pointer",
        },
    };

    return (
        <div style={styles.card}>
            <h2 style={styles.title}>Change Password</h2>

            {/* Current Password */}
            <div style={styles.group}>
                <label style={styles.label}>Current Password *</label>
                <div style={styles.inputWrapper}>
                    <input
                        type={showCurrent ? "text" : "password"}
                        value={current}
                        onChange={(e) => setCurrent(e.target.value)}
                        style={styles.input}
                        placeholder="Enter Current password"
                    />
                    <span style={styles.eye} onClick={() => setShowCurrent(!showCurrent)}>
                        {showCurrent ? <FiEyeOff /> : <FiEye />}
                    </span>
                </div>
            </div>

            {/* New Password */}
            <div style={styles.group}>
                <label style={styles.label}>New Password *</label>
                <div style={styles.inputWrapper}>
                    <input
                        type={showNew ? "text" : "password"}
                        value={newPass}
                        onChange={(e) => checkStrength(e.target.value)}
                        style={styles.input}
                        placeholder="Enter New password"
                    />
                    <span style={styles.eye} onClick={() => setShowNew(!showNew)}>
                        {showNew ? <FiEyeOff /> : <FiEye />}
                    </span>
                </div>
                {strength && (
                    <small style={strength.includes("Strong") ? styles.success : styles.error}>
                        {strength}
                    </small>
                )}
            </div>

            {/* Confirm Password */}
            <div style={styles.group}>
                <label style={styles.label}>Confirm Password *</label>
                <div style={styles.inputWrapper}>
                    <input
                        type={showConfirm ? "text" : "password"}
                        value={confirm}
                        onChange={(e) => checkMatch(e.target.value)}
                        style={styles.input}
                        placeholder="Enter Confirm Password"
                    />
                    <span style={styles.eye} onClick={() => setShowConfirm(!showConfirm)}>
                        {showConfirm ? <FiEyeOff /> : <FiEye />}
                    </span>
                </div>
                {matchMsg && (
                    <small style={matchMsg.includes("✔") ? styles.success : styles.error}>
                        {matchMsg}
                    </small>
                )}
            </div>

            <p style={{ color: "red", fontSize: "12px" }}>
                Password must contain uppercase, lowercase, number & special character.
            </p>

            <button style={styles.button} onClick={handleSubmit}>
                Update Password
            </button>
        </div>
    );
}
